import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const ReportingLeakageChart = ({ filters }) => {
  const [focusMode, setFocusMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const baseUrl = getRoundRobinBaseUrl();

        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) {
            filteredParams[key] = filters[key];
          }
        });

        const response = await api.get(`${baseUrl}/superadminDashboard/getSuperAdminAttendaReportingWiseLeakage`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: filteredParams,
        });

        if (response.data?.success && response.data?.data) {
          const cleanedData = response.data.data.map((item) => ({
            name: item.reportingManagerName,
            leakage: parseFloat(item.leakagePercentage.replace('%', '')),
          }));
          setData(cleanedData);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('Error fetching reporting leakage data:', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
    if (filters) {
      fetchData();
    }
  }, [accessToken, filters]);

  const handleFocusMode = () => {
    setFocusMode(true);
  };

  const LoadingDots = () => (
    <div className="flex space-x-2">
      <div className="w-4 h-4 bg-[#1A2B5C] rounded-full animate-bounce"></div>
      <div className="w-4 h-4 bg-[#EE1D23] rounded-full animate-bounce [animation-delay:0.2s]"></div>
      <div className="w-4 h-4 bg-[#1A2B5C] rounded-full animate-bounce [animation-delay:0.4s]"></div>
    </div>
  );

  const CardWrapper = ({ children, className = '' }) => (
    <div
      className={`relative w-full max-w-6xl mx-auto p-6 rounded-[2rem] backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] bg-white/70 border border-gray-200 overflow-hidden ${className}`}
    >
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#EE1D23]/30 rounded-full blur-[100px] z-0" />
      <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-[#1A2B5C]/30 rounded-full blur-[100px] z-0" />
      <div className="relative z-10">{children}</div>
    </div>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, leakage } = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded shadow border border-[#EE1D23]">
          <p className="text-sm font-bold text-[#1A2B5C]">{name}</p>
          <p className="text-xs text-red-600 font-semibold">Leakage: {leakage.toFixed(2)}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <style>
        {`
          @keyframes slideZoomUp {
            0% { transform: translateY(60%) scale(0.8); opacity: 0; }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }
          .animate-slideZoomUp {
            animation: slideZoomUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          }
        `}
      </style>

      {!focusMode && (
        <CardWrapper>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-[#1A2B5C] uppercase tracking-wide">Reporting Wise Leakage</h3>
              <p className="text-sm text-gray-500 mb-4">Team Leakage Overview</p>
            </div>
            <button
              onClick={handleFocusMode}
              className="px-4 py-2 text-xs font-semibold rounded-full border border-[#1A2B5C] text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white transition"
            >
              Focus Mode
            </button>
          </div>

          <div className="h-[400px] flex items-center justify-center">
            {isLoading ? (
              <LoadingDots />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="gradientLeak" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EE1D23" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#EE1D23" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  {/* <XAxis
                    dataKey="name"
                    angle={-40}
                    textAnchor="end"
                    interval={0}
                    height={80}
                    tick={{ fontSize: 10, fill: '#1A2B5C' }}
                    tickLine={false}
                  /> */}
                  <XAxis
                    dataKey="name"
                    interval={0}
                    height={100}
                    tick={({ x, y, payload }) => {
                      const fullName = payload.value;
                      const displayName = fullName.length > 12 ? fullName.slice(0, 10) + '…' : fullName;
                      return (
                        <g transform={`translate(${x},${y + 10})`}>
                          <title>{fullName}</title>
                          <text textAnchor="end" transform="rotate(-40)" fontSize={14} fill="#1A2B5C">
                            {displayName}
                          </text>
                        </g>
                      );
                    }}
                  />

                  <YAxis tickFormatter={(tick) => `${tick}%`} />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="leakage"
                    stroke="#EE1D23"
                    fill="url(#gradientLeak)"
                    strokeWidth={3}
                    animationDuration={1400}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardWrapper>
      )}

      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-6xl mx-auto animate-slideZoomUp">
            <CardWrapper>
              <button
                onClick={() => setFocusMode(false)}
                className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23] transition-transform hover:scale-110"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#1A2B5C]">Reporting Wise Leakage</h2>
                  <p className="text-sm text-gray-500 mb-4">Focus View</p>
                </div>
                <div className="h-2 w-32 rounded-full bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C]" />
              </div>

              <div className="h-[70vh] flex items-center justify-center">
                {isLoading ? (
                  <LoadingDots />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data}>
                      <defs>
                        <linearGradient id="gradientLeakFocus" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#EE1D23" stopOpacity={0.8} />
                          <stop offset="100%" stopColor="#EE1D23" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        interval={0}
                        height={100}
                        tick={({ x, y, payload }) => {
                          const fullName = payload.value;
                          const displayName = fullName.length > 12 ? fullName.slice(0, 10) + '…' : fullName;
                          return (
                            <g transform={`translate(${x},${y + 10})`}>
                              <title>{fullName}</title>
                              <text textAnchor="end" transform="rotate(-40)" fontSize={14} fill="#1A2B5C">
                                {displayName}
                              </text>
                            </g>
                          );
                        }}
                      />

                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="leakage"
                        stroke="#EE1D23"
                        fill="url(#gradientLeakFocus)"
                        strokeWidth={3}
                        animationDuration={1400}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardWrapper>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportingLeakageChart;
